import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, downloadPdf, formatMoney, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EstadoBadge from "@/components/EstadoBadge";
import EstadoPagoBadge from "@/components/EstadoPagoBadge";
import DatePicker from "@/components/DatePicker";
import ClienteAutocomplete from "@/components/ClienteAutocomplete";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Eye, FilePlus2, FileX2, Download, Copy, CheckCircle2, PackageCheck,
} from "lucide-react";
import { toast } from "sonner";

export default function Pedidos() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [remitosMap, setRemitosMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ desde: "", hasta: "", estado: "ALL", cliente: "", cliente_id: "" });
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.desde) params.desde = filters.desde;
      if (filters.hasta) params.hasta = filters.hasta;
      if (filters.estado && filters.estado !== "ALL") params.estado = filters.estado;
      if (filters.cliente_id) params.cliente_id = filters.cliente_id;
      const [a, b] = await Promise.all([
        api.get("/pedidos", { params }),
        api.get("/remitos"),
      ]);
      setRows(a.data);
      const map = {};
      for (const r of b.data) map[r.pedido_id] = r;
      setRemitosMap(map);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const setBusyFor = (id, val) => setBusy((b) => ({ ...b, [id]: val }));

  const confirmar = async (p) => {
    setBusyFor(p.id, true);
    try {
      const res = await api.post(`/pedidos/${p.id}/confirmar`);
      toast.success(`Remito ${res.data.remito.remito_id} generado.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al confirmar.");
    } finally { setBusyFor(p.id, false); }
  };

  const setEstado = async (p, estado) => {
    setBusyFor(p.id, true);
    try {
      await api.post(`/pedidos/${p.id}/estado`, null, { params: { estado } });
      toast.success(`Pedido ${p.pedido_id} → ${estado.toLowerCase()}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cambiar estado.");
    } finally { setBusyFor(p.id, false); }
  };

  const descargarPdf = async (p) => {
    const remito = remitosMap[p.id];
    if (!remito) {
      toast.error("Confirmá el pedido para generar el remito.");
      return;
    }
    setBusyFor(p.id, true);
    try {
      await downloadPdf(`/remitos/${remito.id}/pdf`, `remito_${remito.remito_id}.pdf`);
    } catch {
      toast.error("Error al descargar PDF.");
    } finally { setBusyFor(p.id, false); }
  };

  const duplicar = async (p) => {
    setBusyFor(p.id, true);
    try {
      const res = await api.post(`/pedidos/${p.id}/duplicar`);
      toast.success(`Pedido duplicado: ${res.data.pedido_id}`);
      navigate(`/admin/pedidos/${res.data.id}`);
    } catch {
      toast.error("Error al duplicar.");
    } finally { setBusyFor(p.id, false); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Operaciones"
        title="Pedidos"
        subtitle="Pedidos enviados por los clientes y los que cargás manualmente. Confirmá y marcá entregados."
      >
        <Link to="/admin/carga-rapida">
          <Button className="bg-[#C85A32] hover:bg-[#A64A29] text-white h-10 gap-2" data-testid="btn-nuevo-pedido">
            <FilePlus2 className="h-4 w-4" /> Carga rápida
          </Button>
        </Link>
      </PageHeader>

      <Card className="p-4 sm:p-5 border-[#E5E0D8] rounded-2xl bg-white mb-5">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-[11px] text-[#595959] mb-1.5 block">Desde</Label>
            <DatePicker value={filters.desde} onChange={(v) => setFilters((f) => ({ ...f, desde: v }))} testid="ped-f-desde" />
          </div>
          <div>
            <Label className="text-[11px] text-[#595959] mb-1.5 block">Hasta</Label>
            <DatePicker value={filters.hasta} onChange={(v) => setFilters((f) => ({ ...f, hasta: v }))} testid="ped-f-hasta" />
          </div>
          <div>
            <Label className="text-[11px] text-[#595959] mb-1.5 block">Estado</Label>
            <Select value={filters.estado} onValueChange={(v) => setFilters((f) => ({ ...f, estado: v }))}>
              <SelectTrigger className="bg-white h-10" data-testid="ped-f-estado"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos</SelectItem>
                <SelectItem value="PENDIENTE">Pendientes</SelectItem>
                <SelectItem value="CONFIRMADO">Confirmados</SelectItem>
                <SelectItem value="ENTREGADO">Entregados</SelectItem>
                <SelectItem value="CANCELADO">Cancelados</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <Label className="text-[11px] text-[#595959] mb-1.5 block">Cliente</Label>
            <ClienteAutocomplete
              value={filters.cliente}
              cliente_id={filters.cliente_id}
              onChange={({ nombre, cliente_id }) => setFilters((f) => ({ ...f, cliente: nombre, cliente_id }))}
              testid="ped-f-cliente"
              placeholder="Todos"
            />
          </div>
          <Button
            variant="outline"
            className="border-[#E5E0D8] hover:bg-[#F9F6F0] h-10"
            onClick={() => setFilters({ desde: "", hasta: "", estado: "ALL", cliente: "", cliente_id: "" })}
            data-testid="ped-f-reset"
          >
            Limpiar
          </Button>
        </div>
      </Card>

      <Card className="border-[#E5E0D8] rounded-2xl bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-[#F9F6F0] border-b border-[#E5E0D8]">
              <tr className="text-[11px] uppercase tracking-[0.14em] text-[#8C8C8C]">
                <th className="px-5 py-3">ID</th>
                <th className="px-5 py-3">Fecha</th>
                <th className="px-5 py-3">Cliente</th>
                <th className="px-5 py-3">Items</th>
                <th className="px-5 py-3 text-right">Total</th>
                <th className="px-5 py-3">Estado</th>
                <th className="px-5 py-3">Pago</th>
                <th className="px-5 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center text-[#8C8C8C] py-12">Cargando...</td></tr>}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-16">
                    <FileX2 className="h-10 w-10 text-[#C85A32] mx-auto mb-3 opacity-50" />
                    <div className="font-display font-semibold text-lg text-[#1A1A1A]">Sin pedidos</div>
                    <p className="text-sm text-[#595959] mt-1">Los pedidos de los clientes aparecen acá.</p>
                  </td>
                </tr>
              )}
              {!loading && rows.map((p, i) => {
                const remito = remitosMap[p.id];
                const isBusy = !!busy[p.id];
                return (
                  <tr key={p.id} className="border-b border-[#F0EBE0] hover:bg-[#FBF8F2] fade-row" style={{ animationDelay: `${i * 20}ms` }} data-testid={`ped-row-${p.pedido_id}`}>
                    <td className="px-5 py-4 font-mono text-sm font-semibold text-[#C85A32]">{p.pedido_id}</td>
                    <td className="px-5 py-4 text-sm text-[#1A1A1A]">{formatDate(p.fecha)}</td>
                    <td className="px-5 py-4 text-sm font-medium text-[#1A1A1A]">{p.cliente}</td>
                    <td className="px-5 py-4 text-sm text-[#595959]">
                      <span className="font-mono text-[#1A1A1A] font-semibold">{p.items.reduce((s, it) => s + Number(it.cantidad), 0)}</span> u · {p.items.length} prod.
                    </td>
                    <td className="px-5 py-4 text-right font-mono font-semibold text-[#1A1A1A]">{formatMoney(p.total)}</td>
                    <td className="px-5 py-4"><EstadoBadge estado={p.estado} /></td>
                    <td className="px-5 py-4">
                      <div className="flex flex-col gap-0.5 items-start">
                        <EstadoPagoBadge estado={p.estado_pago} />
                        {p.estado_pago === "PARCIAL" && (
                          <span className="text-[10px] text-[#8C8C8C] font-mono">
                            Saldo {formatMoney(p.saldo_pendiente)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex justify-end items-center gap-0.5">
                        <Link to={`/admin/pedidos/${p.id}`}>
                          <Button variant="ghost" size="sm" className="h-8 px-2 text-[#595959] hover:text-[#C85A32] hover:bg-[#F4EAE6]" title="Ver detalle" data-testid={`ped-ver-${p.pedido_id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                        {p.estado === "PENDIENTE" && !p.items.some((it) => it.needs_review) && p.items.length > 0 && (
                          <Button
                            variant="ghost" size="sm"
                            className="h-8 px-2 text-[#6B705C] hover:bg-[#E9EBE4]"
                            title="Confirmar pedido"
                            disabled={isBusy}
                            onClick={() => confirmar(p)}
                            data-testid={`ped-confirmar-${p.pedido_id}`}
                          >
                            <CheckCircle2 className="h-4 w-4" />
                          </Button>
                        )}
                        {p.estado === "CONFIRMADO" && (
                          <Button
                            variant="ghost" size="sm"
                            className="h-8 px-2 text-[#3F6B3C] hover:bg-[#DAE6D7]"
                            title="Marcar entregado"
                            disabled={isBusy}
                            onClick={() => setEstado(p, "ENTREGADO")}
                            data-testid={`ped-entregar-${p.pedido_id}`}
                          >
                            <PackageCheck className="h-4 w-4" />
                          </Button>
                        )}
                        {remito && (
                          <Button
                            variant="ghost" size="sm"
                            className="h-8 px-2 text-[#C85A32] hover:bg-[#F4EAE6]"
                            title="Descargar PDF"
                            disabled={isBusy}
                            onClick={() => descargarPdf(p)}
                            data-testid={`ped-pdf-${p.pedido_id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost" size="sm"
                          className="h-8 px-2 text-[#595959] hover:text-[#C85A32] hover:bg-[#F4EAE6]"
                          title="Duplicar pedido"
                          disabled={isBusy}
                          onClick={() => duplicar(p)}
                          data-testid={`ped-duplicar-${p.pedido_id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
